import identity from 'lodash/identity';
import type { NestedFieldValueType } from '@helpcenter/field-types';
import memoizeOne from 'memoize-one';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
	LOOKUP_SOURCE_USERS_ASYNC_DATA,
} from '@atlassian/ui-modifications-core/constants';
import type {
	ViewConfiguration,
	BaseFieldConfiguration,
	AnalyticsResult,
	FieldType,
} from '@atlassian/ui-modifications-core/types';
import { getCommonFieldValidators } from '@atlassian/ui-modifications-core/utils';
import { selectSetValueValidator } from '@atlassian/ui-modifications-public-api-utils/field/select';
import { validateSingleLineTextValue } from '@atlassian/ui-modifications-public-api-utils/field/single-line-text';
import { validateSetOptionsVisibilityRule } from '@atlassian/ui-modifications-public-api-utils/options-visibility';
import type { RequestCreateExecutionContext } from '../../types/context';
import {
	cancelTTI,
	markFieldsRegistered,
	markIframeLoad,
	markOnInitResolved,
	stopTTI,
} from '../performance-analytics/request-create';
import {
	SUMMARY_TYPE,
	SELECT_CF_TYPE,
	USER_CF_TYPE,
	EMBEDDED_REQUEST_CREATE,
	DEFAULT,
	TEXT_CF_TYPE,
	TEXT_AREA_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	PRIORITY_TYPE,
	DATETIME_CF_TYPE,
	DUE_DATE_TYPE,
	NUMBER_CF_TYPE,
	DESCRIPTION_TYPE,
	CASCADING_SELECT_CF_TYPE,
	DATEPICKER_CF_TYPE,
	LABELS_TYPE,
} from './constants';
import {
	validateCascadingSelectSetValue,
	transformSetValueCascadingSelect,
	transformGetValueCascadingSelect,
	transformValueToBasicCascadingSelect,
} from './fields/cascading-select';
import {
	datePickerGetValueTransformer,
	datePickerSetValueTransformer,
	datePickerSetValueValidator,
} from './fields/date-picker';
import {
	validateDateTimePickerSetValue,
	transformDateTimePickerSetValue,
	transformDateTimePickerGetValue,
} from './fields/date-time-picker';
import {
	labelsSetValueValidator,
	labelsSetValueTransformer,
	labelsGetValueTransformer,
	modifyLabelsValueToBasic,
} from './fields/labels';
import {
	validateMultiCheckboxSetValue,
	multiCheckboxSetValueTransformer,
	multiCheckboxGetValueTransformer,
	multiCheckboxSetOptionsVisibilityTransformer,
	multiCheckboxGetOptionsVisibilityTransformer,
	modifyMultiSelectValueToBasic,
} from './fields/multi-checkbox';
import {
	validateNumberValue,
	transformNumberToInternalValue,
	transformNumberToPublicValue,
} from './fields/number';
import {
	prioritySetValueTransformer,
	prioritySetOptionsVisibility,
	priorityGetValueTransformer,
	validatePrioritySetValue,
	modifyPriorityValueToBasic,
} from './fields/priority';
import {
	selectGetValueTransformer,
	transformSelectGetOptionsVisibility,
	transformSelectSetOptionsVisibility,
	selectSetValueTransformer,
	modifySelectValueToBasic,
} from './fields/select';
import { singleLineTextFieldGetValueTransformer } from './fields/single-line-text';
import {
	validateTextAreaValue,
	textAreaGetValueTransformer,
	textAreaSetValueTransformer,
} from './fields/text-area';
import {
	validateUserPickerSetValue,
	userPickerSetValueTransformer,
	userPickerGetValueTransformer,
	modifyUserPickerValueToBasic,
} from './fields/user-picker';
import messages from './messages';
import { validateSetOptionsVisibility } from './utils/options-visibility';

export const isEmbeddedRequestPageEnabled = () =>
	FeatureGates.checkGate('ditto_uim_jsm_embedded_request_page');
export const isTextFieldSupportEnabled = () => FeatureGates.checkGate('uim_jsm_textfield_support');
export const isMultiSelectEnabled = () =>
	FeatureGates.checkGate('ditto_uim_jsm_multi_select_support');
export const isNumberFieldSupportEnabled = () =>
	FeatureGates.checkGate('ditto_uim_jsm_number_field_support');
export const isDueDateSupportEnabled = () => FeatureGates.checkGate('uim_jsm_duedate_support');
export const isDateTimeSupportEnabled = () =>
	FeatureGates.checkGate('ditto_uim_jsm_datetime_field_support');
export const isDescriptionSupportEnabled = () =>
	FeatureGates.checkGate('ditto_uim_jsm_description_support');
export const isCascadingSelectSupportEnabled = () =>
	FeatureGates.checkGate('ditto_uim_jsm_cascading_select_support');
export const isDatePickerSupportEnabled = () =>
	FeatureGates.checkGate('ditto_uim_jsm_datepicker_field_support');
export const isUserPickerSupportEnabled = () =>
	FeatureGates.checkGate('ditto_uim_jsm_user_picker_support');
export interface RequestCreateViewFieldConfiguration extends BaseFieldConfiguration {
	initializeFieldPropertyMapping?: Record<string, string>;
	modifyValueToBasic: (data: unknown) => string | string[] | null | NestedFieldValueType;
}

export class RequestCreateViewConfiguration implements ViewConfiguration<
	RequestCreateViewFieldConfiguration,
	RequestCreateExecutionContext
> {
	private analytics: AnalyticsResult = {
		stopTTI,
		cancelTTI,
		markIframeLoad,
		markFieldsRegistered,
		markOnInitResolved,
	};

	getAnalytics() {
		return this.analytics;
	}

	/**
	 * memoizeOne is used to avoid recreating the config objects.
	 */
	getSupportedFieldsConfiguration = memoizeOne(
		(): Record<FieldType, RequestCreateViewFieldConfiguration> => {
			const commonFieldValidators = getCommonFieldValidators();

			return {
				[SUMMARY_TYPE]: {
					publicShapeValidators: {
						...commonFieldValidators,
						value: validateSingleLineTextValue,
					},
					publicToInternalTransformers: {},
					internalToPublicTransformers: { value: singleLineTextFieldGetValueTransformer },
					emitChangeOn: 'blur',
					modifyValueToBasic: identity,
				},
				[SELECT_CF_TYPE]: {
					publicShapeValidators: {
						...commonFieldValidators,
						value: selectSetValueValidator,
						optionsVisibility: validateSetOptionsVisibilityRule,
					},
					publicToInternalTransformers: {
						value: selectSetValueTransformer,
						optionsVisibility: transformSelectSetOptionsVisibility,
					},
					internalToPublicTransformers: {
						value: selectGetValueTransformer,
						optionsVisibility: transformSelectGetOptionsVisibility,
					},
					lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
					initializeFieldPropertyMapping: { allowedValues: 'options', value: 'defaultValue' },
					propertyMapping: { optionsVisibility: 'options' },
					emitChangeOn: 'change',
					modifyValueToBasic: modifySelectValueToBasic,
				},
				...(isTextFieldSupportEnabled() && {
					[TEXT_CF_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: validateSingleLineTextValue,
						},
						publicToInternalTransformers: {},
						internalToPublicTransformers: { value: singleLineTextFieldGetValueTransformer },
						emitChangeOn: 'blur',
						modifyValueToBasic: identity,
					},
				}),
				...(isNumberFieldSupportEnabled() && {
					[NUMBER_CF_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: validateNumberValue,
						},
						publicToInternalTransformers: {
							value: transformNumberToInternalValue,
						},
						internalToPublicTransformers: {
							value: transformNumberToPublicValue,
						},
						emitChangeOn: 'blur',
						modifyValueToBasic: identity,
					},
				}),
				...(isMultiSelectEnabled() && {
					[MULTI_SELECT_CF_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: validateMultiCheckboxSetValue,
							optionsVisibility: validateSetOptionsVisibilityRule,
						},
						publicToInternalTransformers: {
							value: multiCheckboxSetValueTransformer,
							optionsVisibility: multiCheckboxSetOptionsVisibilityTransformer,
						},
						internalToPublicTransformers: {
							value: multiCheckboxGetValueTransformer,
							optionsVisibility: multiCheckboxGetOptionsVisibilityTransformer,
						},
						emitChangeOn: 'change',
						lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
						initializeFieldPropertyMapping: { allowedValues: 'options', value: 'defaultValue' },
						propertyMapping: { optionsVisibility: 'options' },
						modifyValueToBasic: modifyMultiSelectValueToBasic,
					},
				}),
				[PRIORITY_TYPE]: {
					publicShapeValidators: {
						...commonFieldValidators,
						value: validatePrioritySetValue,
						optionsVisibility: validateSetOptionsVisibility,
					},
					publicToInternalTransformers: {
						value: prioritySetValueTransformer,
						optionsVisibility: prioritySetOptionsVisibility,
					},
					internalToPublicTransformers: {
						value: priorityGetValueTransformer,
						optionsVisibility: identity,
					},
					emitChangeOn: 'change',
					lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
					initializeFieldPropertyMapping: { allowedValues: 'options', value: 'defaultValue' },
					propertyMapping: { optionsVisibility: 'options' },
					modifyValueToBasic: modifyPriorityValueToBasic,
				},
				...(isDueDateSupportEnabled() && {
					[DUE_DATE_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: datePickerSetValueValidator,
						},
						publicToInternalTransformers: {
							value: datePickerSetValueTransformer,
						},
						internalToPublicTransformers: {
							value: datePickerGetValueTransformer,
						},
						emitChangeOn: 'change',
						modifyValueToBasic: identity,
					},
				}),
				...(isDateTimeSupportEnabled() && {
					[DATETIME_CF_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: validateDateTimePickerSetValue,
						},
						publicToInternalTransformers: {
							value: transformDateTimePickerSetValue,
						},
						internalToPublicTransformers: {
							value: transformDateTimePickerGetValue,
						},
						emitChangeOn: 'change',
						modifyValueToBasic: identity,
					},
				}),
				[TEXT_AREA_CF_TYPE]: {
					publicShapeValidators: {
						...commonFieldValidators,
						value: validateTextAreaValue,
					},
					publicToInternalTransformers: {
						value: textAreaSetValueTransformer,
					},
					internalToPublicTransformers: {
						value: textAreaGetValueTransformer,
					},
					emitChangeOn: 'blur',
					modifyValueToBasic: identity,
				},
				...(isDescriptionSupportEnabled() && {
					[DESCRIPTION_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: validateTextAreaValue,
						},
						publicToInternalTransformers: {
							value: textAreaSetValueTransformer,
						},
						internalToPublicTransformers: {
							value: textAreaGetValueTransformer,
						},
						emitChangeOn: 'blur',
						modifyValueToBasic: identity,
					},
				}),
				...(isCascadingSelectSupportEnabled() && {
					[CASCADING_SELECT_CF_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: validateCascadingSelectSetValue,
						},
						publicToInternalTransformers: {
							value: transformSetValueCascadingSelect,
						},
						internalToPublicTransformers: {
							value: transformGetValueCascadingSelect,
						},
						emitChangeOn: 'change',
						lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
						initializeFieldPropertyMapping: { allowedValues: 'options', value: 'defaultValue' },
						modifyValueToBasic: transformValueToBasicCascadingSelect,
					},
				}),
				...(isDatePickerSupportEnabled() && {
					[DATEPICKER_CF_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: datePickerSetValueValidator,
						},
						publicToInternalTransformers: {
							value: datePickerSetValueTransformer,
						},
						internalToPublicTransformers: {
							value: datePickerGetValueTransformer,
						},
						emitChangeOn: 'change',
						modifyValueToBasic: identity,
					},
				}),
				[LABELS_TYPE]: {
					publicShapeValidators: {
						...commonFieldValidators,
						value: labelsSetValueValidator,
					},
					publicToInternalTransformers: {
						value: labelsSetValueTransformer,
					},
					internalToPublicTransformers: {
						value: labelsGetValueTransformer,
					},
					emitChangeOn: 'change',
					initializeFieldPropertyMapping: { value: 'defaultLabels' },
					modifyValueToBasic: modifyLabelsValueToBasic,
				},
				...(isUserPickerSupportEnabled() && {
					[USER_CF_TYPE]: {
						publicShapeValidators: {
							...commonFieldValidators,
							value: validateUserPickerSetValue,
						},
						publicToInternalTransformers: {
							value: userPickerSetValueTransformer,
						},
						internalToPublicTransformers: {
							value: userPickerGetValueTransformer,
						},
						emitChangeOn: 'change',
						lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
						initializeFieldPropertyMapping: { value: 'defaultUser' },
						modifyValueToBasic: modifyUserPickerValueToBasic,
					},
				}),
				//
				/**
				 * These are the next fields to be implemented
				 * Let's keep this code here for reference
				 * */

				// [USER_CF_TYPE]: {
				//     publicShapeValidators: {
				//         ...commonFieldValidators,
				//         value: () => true,
				//     },
				//     publicToInternalTransformers: {
				//         value: (value: string) => ({
				//             basic: value,
				//             details: {
				//                 value,
				//                 accountId: value,
				//                 label: `The fake user name of ${value} since there is no real UIM`,
				//             },
				//         }),
				//     },
				//     initializeFieldProprtyMapping: {
				//         value: 'defaultUser',
				//     },
				//     emitChangeOn: 'change',
				// },
			};
		},
	);

	getSupportedFieldTypes = memoizeOne(() => {
		const supportedFieldTypes: string[] = [
			SUMMARY_TYPE,
			SELECT_CF_TYPE,
			USER_CF_TYPE,
			TEXT_CF_TYPE,
			TEXT_AREA_CF_TYPE,
			DESCRIPTION_TYPE,
			NUMBER_CF_TYPE,
			MULTI_SELECT_CF_TYPE,
			PRIORITY_TYPE,
			DATETIME_CF_TYPE,
			DUE_DATE_TYPE,
			CASCADING_SELECT_CF_TYPE,
			DATEPICKER_CF_TYPE,
			LABELS_TYPE,
		];

		return supportedFieldTypes;
	});

	/**
	 * On other views in Jira, we check if we support a specific project type available from execution context
	 * Here, in JSM, there is nothing specific available from Execution Context that we should check
	 */
	isExecutionContextSupported(): boolean {
		return true;
	}

	getConsentMessage() {
		return messages.obligatoryUimRequestCreateConsentMessage;
	}

	getSupportedTriggerPointsConfiguration() {
		if (isEmbeddedRequestPageEnabled()) {
			return new Set([DEFAULT, EMBEDDED_REQUEST_CREATE] as const);
		}
		return new Set([DEFAULT] as const);
	}

	// Note: should be removed by bumping @atlassian/ui-modifications-core to the next minor version. Behavior will default to being true.
	isScreenTabsSupportEnabled = () => true;
}
